import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

const SESSION_TAG = "StockCheckSession";

export const stockCheckApi = createApi({
  reducerPath: "stockCheckApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { acceptJson: true, contentTypeJson: true }),
  }),
  tagTypes: [SESSION_TAG],
  endpoints: (builder) => ({
    // ── Queries ──────────────────────────────────────────────────────────────

    fetchStockCheckSessions: builder.query({
      query: (params = {}) => {
        const sp = new URLSearchParams();
        Object.entries(params || {}).forEach(([k, v]) => {
          if (v === undefined || v === null || v === "") return;
          sp.set(k, String(v));
        });
        const qs = sp.toString();
        return qs
          ? `stock-check/sessions?${qs}`
          : "stock-check/sessions";
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (result) =>
        (result?.items || []).map((s) => ({ type: SESSION_TAG, id: s.id })),
    }),

    fetchStockCheckSession: builder.query({
      query: (sessionId) => `stock-check/sessions/${sessionId}`,
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (_result, _err, sessionId) => [
        { type: SESSION_TAG, id: sessionId },
      ],
    }),

    fetchStockCheckReport: builder.query({
      query: (sessionId) => `stock-check/sessions/${sessionId}/report`,
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (_result, _err, sessionId) => [
        { type: SESSION_TAG, id: `report-${sessionId}` },
      ],
    }),

    // ── Mutations ─────────────────────────────────────────────────────────────

    startStockCheckSession: builder.mutation({
      query: (body) => ({
        url: "stock-check/sessions",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: [{ type: SESSION_TAG, id: "LIST" }],
    }),

    scanStockCheckBarcode: builder.mutation({
      query: ({ sessionId, barcode }) => ({
        url: `stock-check/sessions/${sessionId}/scan`,
        method: "POST",
        body: { barcode },
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      // Invalidate the specific session so items/stats refresh
      invalidatesTags: (_result, _err, { sessionId }) => [
        { type: SESSION_TAG, id: sessionId },
      ],
    }),

    undoStockCheckScan: builder.mutation({
      query: (sessionId) => ({
        url: `stock-check/sessions/${sessionId}/undo-last-scan`,
        method: "POST",
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _err, sessionId) => [
        { type: SESSION_TAG, id: sessionId },
      ],
    }),

    completeStockCheckSession: builder.mutation({
      query: (sessionId) => ({
        url: `stock-check/sessions/${sessionId}/complete`,
        method: "POST",
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _err, sessionId) => [
        { type: SESSION_TAG, id: sessionId },
        { type: SESSION_TAG, id: "LIST" },
      ],
    }),

    cancelStockCheckSession: builder.mutation({
      query: (sessionId) => ({
        url: `stock-check/sessions/${sessionId}/cancel`,
        method: "POST",
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (_result, _err, sessionId) => [
        { type: SESSION_TAG, id: sessionId },
        { type: SESSION_TAG, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useFetchStockCheckSessionsQuery,
  useFetchStockCheckSessionQuery,
  useFetchStockCheckReportQuery,
  useStartStockCheckSessionMutation,
  useScanStockCheckBarcodeMutation,
  useUndoStockCheckScanMutation,
  useCompleteStockCheckSessionMutation,
  useCancelStockCheckSessionMutation,
} = stockCheckApi;
